import React, { useRef, useState } from 'react';
import moment from 'moment';
import type { Moment, MomentFormatSpecification } from 'moment';
import { t } from 'i18next';
import { FormControl } from '../../FormComponents/FormControl';
import { FormInput } from '../../FormComponents/Input/Input';
import DateTimeEngine from './_components/DateTimeEngine';
import type { DateTimeEngineFieldProps } from './_components/DateTimeEngine.types';
import { POSITIONING_STRATEGY } from '../_hooks/usePositioning';
import type { DatePickerProps } from './DatePicker.types';

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_TIME_FORMAT = 'HH:mm';

const TYPED_INPUT_FORMATS = ['YYYYMMDD', 'YYYYMMDD HH:mm', 'YYYYMMDD HHmm', 'YYYY-MM-DD HHmm'];

const parseTypedInput = (value: string, utc: boolean): Moment | undefined => {
    const m = utc ? moment.utc(value, TYPED_INPUT_FORMATS, true) : moment(value, TYPED_INPUT_FORMATS, true);
    return m.isValid() ? m : undefined;
};

const getTimezoneName = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return `UTC${moment().format('Z')}`;
    }
};

const DatePicker = (props: DatePickerProps) => {
    const [showInvalidText, setShowInvalidText] = useState(false);
    const textIsInvalidRef = useRef(false);

    const flagError = props.field !== undefined ? props.fieldErrorFlags?.[props.field] : undefined;
    const suppliedError = flagError !== undefined && flagError !== '' ? flagError : props.error;
    const error = suppliedError ?? (showInvalidText ? t('dateTimePicker.invalidDate') : undefined);

    const handleChange = (value: Moment | string) => {
        const parsed = moment.isMoment(value) ? value : parseTypedInput(value, props.utc === true);

        if (parsed !== undefined) {
            textIsInvalidRef.current = false;
            setShowInvalidText(false);

            const formatted = props.dateFormat === false ? parsed.format('HH:mm') : props.showTime === false ? parsed.format('YYYY-MM-DD') : parsed.format('YYYY-MM-DD HH:mm');
            props.onChange(formatted, parsed.toDate());
            props.onValidityChange?.(true);
        } else {
            textIsInvalidRef.current = typeof value === 'string' && value.trim() !== '';
            if (!textIsInvalidRef.current) setShowInvalidText(false);
            props.onChange('', null);
            props.onValidityChange?.(!textIsInvalidRef.current);
        }
    };

    const handleBlur = () => setShowInvalidText(textIsInvalidRef.current);

    moment.updateLocale('en', {
        week: {
            dow: 1,
        },
    });

    if (props.readonly === true) {
        const text = props.value === null || props.value === undefined ? undefined : String(props.value);
        return <FormInput readOnly label={props.label} value={text} onChange={() => undefined} />;
    }

    const timeFormat = props.showTime === false ? '' : DEFAULT_TIME_FORMAT;
    const dateFormat: string | false = props.showTime === false ? DEFAULT_DATE_FORMAT : (props.dateFormat ?? DEFAULT_DATE_FORMAT);
    const closeOnSelect = props.closeOnSelect !== false;
    const placeHolderDateFormat =
        props.placeholderText !== undefined && props.placeholderText !== ''
            ? props.placeholderText
            : props.showTime === false
              ? DEFAULT_DATE_FORMAT
              : `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT}`;

    const parse = (value: string | number | Date, formats?: MomentFormatSpecification): Moment => {
        if (formats === undefined) {
            return props.utc === true ? moment.utc(value) : moment(value);
        }
        return props.utc === true ? moment.utc(value, formats, true) : moment(value, formats, true);
    };

    const normalizeValue = (v: unknown): Moment | undefined => {
        if (v === null || v === undefined) return undefined;
        if (moment.isMoment(v)) return v;
        if (typeof v === 'string') {
            const formats: MomentFormatSpecification = [
                dateFormat,
                placeHolderDateFormat,
                `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT}`,
                DEFAULT_DATE_FORMAT,
                DEFAULT_TIME_FORMAT,
            ]
                .filter((f): f is string => typeof f === 'string')
                .concat(TYPED_INPUT_FORMATS)
                .concat(moment.ISO_8601 as unknown as string);
            const m = parse(v, formats);
            return m.isValid() ? m : undefined;
        }
        if (v instanceof Date || typeof v === 'number') {
            const m = parse(v);
            return m.isValid() ? m : undefined;
        }
        return undefined;
    };

    const dtProps = {
        dateFormat: dateFormat,
        timeFormat: timeFormat,
        inputProps: { placeholder: placeHolderDateFormat },
        onChange: handleChange,
        closeOnSelect: closeOnSelect,
        value: normalizeValue(props.value),
        initialValue: normalizeValue(props.initialValue),
        ...(props.isValidDate !== undefined && { isValidDate: props.isValidDate }),
        ...(props.utc !== undefined && { utc: props.utc }),
    };

    return (
        <FormControl controls={{ error, required: props.required }}>
            {props.label !== undefined && props.label !== null && <label>{props.label}</label>}
            <DateTimeEngine
                {...dtProps}
                strategy={props.strategy ?? POSITIONING_STRATEGY.AUTO}
                renderField={(riProps: DateTimeEngineFieldProps, ref) => {
                    return (
                        <div className="d-flex">
                            <input {...riProps} ref={ref} onBlur={handleBlur} className="form-control rounded-8" value={riProps.value ?? ''} />
                        </div>
                    );
                }}
            />
            {props.showTimezone === true && (
                <small className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                    {t('dateTimePicker.timezone')}: {getTimezoneName()}
                </small>
            )}
        </FormControl>
    );
};

export default DatePicker;
